import { Spin } from 'antd';
import { Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { PermissionEn } from 'src/constants/permission.enum';
import Forbidden from 'src/pages/Error/Forbidden';
import type { IResourceFullAccessDetail } from 'src/types/master_data/userResource.type';
import { getAccessTokenFromLS } from 'src/utils/auth';
import { signinRedirect } from 'src/apis/auth.api';
import { usePermissionNew } from './usePermission';

// Import organized routes
import { allRoutes, featRoutes } from 'src/routes';
import type { RouteType } from 'src/routes';

export default function useRouteElements(
  resources: IResourceFullAccessDetail[],
  resourcePermissionLoading: boolean,
  isSupportUser: boolean
) {
  const permissionResource = usePermissionNew(
    featRoutes.map((x) => x.path),
    resources
  );

  const hasPermission = (path: string, permission: PermissionEn) => {
    const similarResource = permissionResource?.filter((x) => path?.includes(x?.path as string));
    const mostSimilarPath = similarResource?.reduce((prevPath, currPath) => {
      return (currPath?.path?.length || 0) > (prevPath?.path?.length || 0) ? currPath : prevPath;
    }, similarResource[0]);
    if (mostSimilarPath) {
      return mostSimilarPath?.permissions?.includes(permission);
    }
    return false;
  };

  // Helper function to create route element with proper error handling and permissions
  const createRouteElement = (route: RouteType) => {
    const {
      path,
      redirect,
      Layout,
      SubLayout,
      Component,
      permission,
      isUsePathV2,
      hasSidebar,
      hasSubSidebar,
      hasSubHeader,
      isHideDetailLayout,
      isDisableFooter
    } = route;

    // Handle redirect routes
    if (redirect) {
      return {
        path,
        element: <Navigate to={redirect} replace />
      };
    }

    // Create the component with permission and path checks
    const createComponent = () => {
      if (!Component) return <></>;

      const ComponentElement = <Component />;

      // Check permissions if required
      if (permission && !hasPermission(path, permission)) {
        return <Forbidden />;
      }

      return ComponentElement;
    };

    const wrappedComponent = (
      <Suspense>
        {Layout ? (
          <Layout isDisableFooter={isDisableFooter}>
            <Suspense>
              {SubLayout ? (
                <SubLayout
                  hasSidebar={hasSidebar}
                  hasSubHeader={hasSubHeader}
                  hasSubSidebar={hasSubSidebar}
                  isHideDetailLayout={isHideDetailLayout}
                  isUsePathV2={isUsePathV2}
                >
                  {createComponent()}
                </SubLayout>
              ) : (
                createComponent()
              )}
            </Suspense>
          </Layout>
        ) : (
          createComponent()
        )}
      </Suspense>
    );

    return {
      path,
      element: wrappedComponent
    };
  }; // Convert routes to route elements
  const routeElements = allRoutes.map((route) => createRouteElement(route));
  // Always call useRoutes hook first to avoid conditional hook call
  const routes = useRoutes(routeElements);

  // Check authentication first
  const token = getAccessTokenFromLS();

  // If no token and not on auth routes, redirect to login
  if (!token && !window.location.pathname.includes('/signin-oidc') && !window.location.pathname.includes('/signout')) {
    signinRedirect(window.location.pathname);
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin spinning={true} />
      </div>
    );
  }

  // Show loading spinner while resources are loading (only if user is authenticated)

  if (token && !isSupportUser && (resourcePermissionLoading || !resources)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin spinning={!!resourcePermissionLoading || !resources} />
      </div>
    );
  }

  return routes;
}
